import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Table, Button, Space, Popconfirm, Typography, Select, Row, Col, DatePicker,
    Spin, Alert, Descriptions, message, List,
    Upload, Modal, Badge, Tag, Collapse, Avatar
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined, CalendarOutlined, UserOutlined,
    UploadOutlined, DownloadOutlined,
    UsergroupAddOutlined, ClockCircleOutlined, AuditOutlined, StarFilled,
    WarningOutlined, ReloadOutlined
} from '@ant-design/icons';
import Papa from 'papaparse';
import {
    fetchCycles, deleteCycle,
    importCyclesBatch
} from '../../redux/slices/planning/planningThunks';
import { getDelegatesForPlanning } from '../../services/delegateService';
import { getAllDoctorConflicts } from '../../services/planning.service';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import CycleFormModal from './CycleFormModal';
import axiosInstance from '../../services/axiosServicesVisit';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { Title, Text, Link } = Typography;
const { RangePicker } = DatePicker;
const dateFormat = 'YYYY-MM-DD';

const formatDate = (dateString) => { 
  if (!dateString) return 'N/D'; 
  const d = dayjs(dateString); 
  return d.isValid() ? d.format('DD/MM/YYYY') : 'Inv.'; 
};

const getCyclePeriod = (weeks) => { 
  if (!weeks || !Array.isArray(weeks) || weeks.length === 0) { 
    return { overallStartDate: null, overallEndDate: null }; 
  } 
  const sortedWeeks = [...weeks].sort((a, b) => (a.week_number ?? Infinity) - (b.week_number ?? Infinity)); 
  const firstWeekStartDate = sortedWeeks[0]?.start_date; 
  const lastWeekEndDate = sortedWeeks[sortedWeeks.length - 1]?.end_date; 
  return { overallStartDate: firstWeekStartDate, overallEndDate: lastWeekEndDate }; 
};

function CyclesPage() {
  const dispatch = useDispatch();
  const allCycles = useSelector((state) => state.planning?.cycles || []);
  const loadingCycles = useSelector((state) => state.planning?.loading?.cycles || false);
  const planningError = useSelector((state) => state.planning?.error);

  const [delegatesList, setDelegatesList] = useState([]);
  const [loadingDelegates, setLoadingDelegates] = useState(true);
  const [errorDelegates, setErrorDelegates] = useState(null);
  const [selectedDelegateFilter, setSelectedDelegateFilter] = useState(undefined);
  const [selectedDateRangeFilter, setSelectedDateRangeFilter] = useState([null, null]);
  const [isAddEditModalVisible, setIsAddEditModalVisible] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);

  const [uploadingCycles, setUploadingCycles] = useState(false);
  const [cycleFileList, setCycleFileList] = useState([]);
  const [parsedCycleData, setParsedCycleData] = useState(null);
  const [parseCycleError, setParseCycleError] = useState(null);

  const [isPersonUploadModalVisible, setIsPersonUploadModalVisible] = useState(false);
  const [currentCycleForPersonManagement, setCurrentCycleForPersonManagement] = useState(null);
  const [uploadingPersons, setUploadingPersons] = useState(false);
  const [personUploadResult, setPersonUploadResult] = useState(null);
  const [personUploadFileList, setPersonUploadFileList] = useState([]);
  const [fileToUploadForPersons, setFileToUploadForPersons] = useState(null); 

  const [isWeeklyPlanUploadModalVisible, setIsWeeklyPlanUploadModalVisible] = useState(false);
  const [weeklyPlanFile, setWeeklyPlanFile] = useState(null);
  const [uploadingWeeklyPlan, setUploadingWeeklyPlan] = useState(false);

  // --- VISITES RAPPROCHÉES (< 5j) ---
  const [frequentVisitsAlerts, setFrequentVisitsAlerts] = useState([]);
  const [loadingFrequentVisits, setLoadingFrequentVisits] = useState(false);

  // --- CONFLITS D'ASSIGNATION DE MÉDECINS ---
  const [assignmentConflicts, setAssignmentConflicts] = useState([]);
  const [loadingConflicts, setLoadingConflicts] = useState(false);

  // --- PARCOURS DU MÉDECIN (MODAL) ---
  const [doctorModalVisible, setDoctorModalVisible] = useState(false);
  const [doctorDetailData, setDoctorDetailData] = useState([]);
  const [doctorLoading, setDoctorLoading] = useState(false);

  const isMounted = useRef(true);

  useEffect(() => { 
    isMounted.current = true; 
    return () => { isMounted.current = false; }; 
  }, []);

  useEffect(() => {
    setLoadingDelegates(true); 
    setErrorDelegates(null);
    getDelegatesForPlanning()
      .then(r => { 
          if (!isMounted.current) return;
          const data = Array.isArray(r?.data) ? r.data : [];
          setDelegatesList(data); 
      })
      .catch(e => { 
        if (!isMounted.current) return; 
        setErrorDelegates("Err délég."); 
        setDelegatesList([]); 
      })
      .finally(() => { 
        if (isMounted.current) setLoadingDelegates(false); 
      });
  }, []);

  useEffect(() => { 
    dispatch(fetchCycles()); 
  }, [dispatch]);

  const handleDelete = (id) => { 
    dispatch(deleteCycle(id)).unwrap()
      .then(() => message.success('Cycle supprimé.'))
      .catch(() => message.error('Erreur suppression.')); 
  };

  const showAddModal = () => { 
    setEditingCycle(null); 
    setIsAddEditModalVisible(true); 
  };

  const showEditModal = (cycle) => { 
    if (cycle && cycle.id != null) { 
      setEditingCycle(cycle); 
      setIsAddEditModalVisible(true); 
    } else { 
      message.error("Données cycle invalides."); 
    } 
  };

  const handleAddEditCancel = () => { 
    setIsAddEditModalVisible(false); 
    setEditingCycle(null); 
  };

  const handleDelegateFilterChange = (value) => { 
    setSelectedDelegateFilter(value); 
  };

  const handleDateRangeFilterChange = (dates) => { 
    setSelectedDateRangeFilter(dates || [null, null]); 
  };

  // ---------------------------------------------------------
  // UPLOAD CSV CYCLES BATCH
  // ---------------------------------------------------------
  const handleBeforeCycleUpload = (file) => { 
    const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv'); 
    if (!isCsv) { 
      message.error('Fichier CSV uniquement!'); 
      return Upload.LIST_IGNORE; 
    } 
    setCycleFileList([file]); 
    setParsedCycleData(null); 
    setParseCycleError(null); 
    setUploadingCycles(true); 
    
    Papa.parse(file, { 
      header: true, 
      skipEmptyLines: true, 
      complete: (results) => { 
        if (!isMounted.current) return; 
        if (results.errors.length > 0) { 
          const errorMsg = `Erreur parsing Ligne ${results.errors[0].row + 1}: ${results.errors[0].message}`; 
          setParseCycleError(errorMsg); 
          message.error("Erreur lecture CSV (voir détails)."); 
          setParsedCycleData(null); 
          setCycleFileList([]); 
        } else if (!results.data || results.data.length === 0) { 
          setParseCycleError("Fichier CSV vide."); 
          message.warn("Fichier CSV vide."); 
          setParsedCycleData([]); 
        } else { 
          const requiredHeaders = ['delegateEmail', 'objectifSemaine', 'debutS1', 'finS1']; 
          const actualHeaders = results.meta.fields; 
          const missingHeaders = requiredHeaders.filter(h => !actualHeaders?.includes(h)); 
          if (missingHeaders.length > 0) { 
            const errorMsg = `Colonnes manquantes pour import de cycle: ${missingHeaders.join(', ')}`; 
            setParseCycleError(errorMsg); 
            message.error(errorMsg); 
            setParsedCycleData(null); 
            setCycleFileList([]); 
          } else { 
            setParsedCycleData(results.data); 
            setParseCycleError(null); 
            message.success(`${results.data.length} cycles prêts pour import.`); 
          } 
        } 
        setUploadingCycles(false); 
      }, 
      error: (error) => { 
        if (!isMounted.current) return; 
        setParseCycleError("Erreur lecture fichier."); 
        message.error("Erreur lecture fichier CSV."); 
        setParsedCycleData(null); 
        setCycleFileList([]); 
        setUploadingCycles(false); 
      } 
    }); 
    return false; 
  };

  const uploadCycleProps = { 
    onRemove: () => { 
      setCycleFileList([]); 
      setParsedCycleData(null); 
      setParseCycleError(null); 
      return true; 
    }, 
    beforeUpload: handleBeforeCycleUpload, 
    fileList: cycleFileList, 
    maxCount: 1, 
    accept: ".csv", 
  };

  const handleImportParsedCycleData = () => { 
    if (!parsedCycleData || parsedCycleData.length === 0 || parseCycleError || uploadingCycles) { 
      if (parseCycleError) message.error(`Corrigez l'erreur fichier: ${parseCycleError}`); 
      else message.error("Aucune donnée de cycle valide à importer."); 
      return; 
    } 
    setUploadingCycles(true); 
    dispatch(importCyclesBatch(parsedCycleData)).unwrap()
      .then((result) => { 
        if (!isMounted.current) return; 
        const createdCount = result?.created ?? 0; 
        const errorCount = result?.errors ?? 0; 
        const details = result?.errorDetails || []; 
        if (errorCount > 0) { 
          message.error(`Import de cycles terminé avec ${errorCount} erreur(s). Voir détails.`); 
          Modal.error({ 
            title: `Échec import pour ${errorCount} cycle(s)`, 
            content: ( 
              <div style={{ marginTop: 16 }}> 
                <p>Erreurs rencontrées :</p> 
                <List size="small" bordered dataSource={details} renderItem={(item) => <List.Item><Text type="danger">{item}</Text></List.Item>} style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '10px' }} /> 
              </div> 
            ), 
            width: 600, 
            maskClosable: true 
          }); 
        } else { 
          message.success(`Importation de cycles réussie ! ${createdCount} cycle(s) créé(s).`); 
        } 
        dispatch(fetchCycles()); 
      })
      .catch(() => {})
      .finally(() => { 
        if (isMounted.current) { 
          setUploadingCycles(false); 
          setCycleFileList([]); 
          setParsedCycleData(null); 
          setParseCycleError(null); 
        } 
      }); 
  };

  // ---------------------------------------------------------
  // UPLOAD CSV PERSONNES ASSIGNÉES
  // ---------------------------------------------------------
  const handleOpenPersonUploadModal = (cycle) => {
    setCurrentCycleForPersonManagement(cycle);
    setPersonUploadResult(null);
    setPersonUploadFileList([]);
    setFileToUploadForPersons(null);
    setIsPersonUploadModalVisible(true);
  };

  const handlePersonUploadModalCancel = () => {
    setIsPersonUploadModalVisible(false);
    setCurrentCycleForPersonManagement(null);
    setPersonUploadResult(null);
    setPersonUploadFileList([]);
    setFileToUploadForPersons(null);
  };

  const handleBeforePersonUpload = (file) => {
    const isCSV = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    if (!isCSV) {
        message.error(`${file.name} n'est pas un fichier CSV.`);
        setFileToUploadForPersons(null);
        setPersonUploadFileList([]);
        return Upload.LIST_IGNORE;
    }
    setFileToUploadForPersons(file);
    setPersonUploadFileList([file]);
    setPersonUploadResult(null);
    return false;
  };

  const triggerManualPersonUpload = async () => {
    if (!fileToUploadForPersons) {
        message.error("Veuillez d'abord sélectionner un fichier CSV.");
        return;
    }
    if (!currentCycleForPersonManagement?.id) {
        message.error("Erreur: Cycle non identifié pour l'assignation des personnes.");
        return;
    }

    const formData = new FormData();
    formData.append('file', fileToUploadForPersons);

    setUploadingPersons(true);
    setPersonUploadResult(null);
    const uploadUrl = `/v1/cycles/${currentCycleForPersonManagement.id}/persons/upload-csv`;

    try {
        const response = await axiosInstance.post(uploadUrl, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success(response.data.message || 'Fichier de personnes traité avec succès.');
        setPersonUploadResult(response.data);
        dispatch(fetchCycles());
        setFileToUploadForPersons(null); 
        setPersonUploadFileList([]); 
    } catch (error) {
        const apiErrorMsg = error.response?.data?.message || 'Erreur lors de l\'upload du fichier des personnes.';
        message.error(apiErrorMsg);
        setPersonUploadResult({ message: apiErrorMsg, errors: [error.response?.data?.detail || error.response?.data?.message || String(error)] });
    } finally {
        if (isMounted.current) {
            setUploadingPersons(false);
        }
    }
  };

  const personUploadProps = {
    name: 'file',
    multiple: false,
    fileList: personUploadFileList,
    onRemove: () => {
        setPersonUploadFileList([]);
        setFileToUploadForPersons(null);
        setPersonUploadResult(null);
        return true;
    },
    beforeUpload: handleBeforePersonUpload,
    accept: ".csv",
    disabled: uploadingPersons,
  };

  // ---------------------------------------------------------
  // PLAN HEBDOMADAIRE (CSV)
  // ---------------------------------------------------------
  const handleDownloadWeeklyPlan = async (cycleId, cycleName) => {
    message.loading({ content: 'Préparation du téléchargement...', key: 'downloadPlan', duration: 0 });
    try {
        const response = await axiosInstance.get(`/v1/cycles/${cycleId}/weekly-plan/download-csv`, {
            responseType: 'blob',
        });
        const safeName = cycleName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || `cycle_${cycleId}`;
        const filename = `plan_hebdomadaire_${safeName}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success({ content: 'Téléchargement démarré.', key: 'downloadPlan', duration: 2 });
    } catch (error) {
        message.error({ content: 'Erreur lors du téléchargement.', key: 'downloadPlan', duration: 3 });
    }
  };

  const handleOpenWeeklyPlanUploadModal = (cycle) => {
    setCurrentCycleForPersonManagement(cycle);
    setIsWeeklyPlanUploadModalVisible(true);
    setWeeklyPlanFile(null);
  };

  const handleWeeklyPlanUploadModalCancel = () => {
    setIsWeeklyPlanUploadModalVisible(false);
    setCurrentCycleForPersonManagement(null);
  };

  const handleBeforeWeeklyPlanUpload = (file) => {
    setWeeklyPlanFile(file);
    return false;
  };

  const triggerManualWeeklyPlanUpload = async () => {
    if (!weeklyPlanFile || !currentCycleForPersonManagement?.id) return;
    
    const formData = new FormData();
    formData.append('file', weeklyPlanFile);
    setUploadingWeeklyPlan(true);
    
    try {
        const url = `/v1/cycles/${currentCycleForPersonManagement.id}/weekly-plan/upload-csv`;
        await axiosInstance.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        message.success('Plan hebdomadaire mis à jour avec succès !');
        dispatch(fetchCycles());
        setIsWeeklyPlanUploadModalVisible(false);
    } catch (error) {
        const apiErrorMsg = error.response?.data?.message || 'Erreur lors de la mise à jour.';
        message.error(apiErrorMsg);
    } finally {
        if (isMounted.current) {
            setUploadingWeeklyPlan(false);
        }
    }
  };

  // ---------------------------------------------------------
  // ALERTES 1 : VISITES RAPPROCHÉES (< 5j)
  // ---------------------------------------------------------
  const fetchFrequentVisits = useCallback(async () => {
    setLoadingFrequentVisits(true);
    try {
      const params = {};
      if (selectedDelegateFilter) params.delegateId = selectedDelegateFilter;
      if (selectedDateRangeFilter && selectedDateRangeFilter[0] && selectedDateRangeFilter[1]) {
        params.startDate = selectedDateRangeFilter[0].format('YYYY-MM-DD');
        params.endDate = selectedDateRangeFilter[1].format('YYYY-MM-DD');
      }
      const res = await axiosInstance.get('/v1/dashboard/alerts/frequent-visits', { params });
      setFrequentVisitsAlerts(res.data || []);
    } catch (e) {
      console.error("Erreur chargement visites rapprochées :", e);
    } finally {
      setLoadingFrequentVisits(false);
    }
  }, [selectedDelegateFilter, selectedDateRangeFilter]);

  useEffect(() => {
    fetchFrequentVisits();
  }, [fetchFrequentVisits]);

  const handleExportFrequentVisits = () => {
    if (!frequentVisitsAlerts || frequentVisitsAlerts.length === 0) {
      message.warning("Aucune visite rapprochée à exporter.");
      return;
    }

    const headers = [
      "Médecin", "Spécialité", "Région", "Partenaire", "Nombre Visites", "Écart Total", 
      "Type Alerte", "Délégués Impliqués", "Visite N°", "Date et Heure Visite", 
      "Délégué Visiteur", "Cycle Actif", "Semaine Cycle", "Période Semaine", "Statut Planning"
    ];

    const rows = [];
    frequentVisitsAlerts.forEach(item => {
      const alertType = item.isSameDelegate 
        ? `Même délégué (${item.delegatesNamesList?.[0] || item.delegate1Name})`
        : `Délégués différents (${item.uniqueDelegatesCount || 2})`;
      const delegatesList = item.delegatesNamesList ? item.delegatesNamesList.join(' & ') : `${item.delegate1Name} & ${item.delegate2Name}`;

      (item.visits || []).forEach(v => {
        let planningStatus = "Hors cycle actif";
        let cycleName = "N/A";
        let weekStr = "N/A";
        let periodStr = "N/A";

        if (v.context) {
          cycleName = v.context.cycleName || "N/A";
          weekStr = `Semaine ${v.context.weekNumber}`;
          periodStr = `${v.context.startShort} au ${v.context.endShort}`;
          if (v.context.isPlannedThisWeek) planningStatus = "Prévu cette semaine";
          else if (v.context.plannedInOtherWeek) planningStatus = `Prévu en Semaine ${v.context.plannedInOtherWeek}`;
          else planningStatus = "Hors planning cycle";
        }

        rows.push([
          `"${item.name || ''}"`, `"${item.specialtyName || ''}"`, `"${item.regionName || ''}"`,
          item.isPartner ? "OUI" : "NON", item.totalVisitsCount || 2, `"${item.totalSpanDays || item.daysDiff}"`,
          `"${alertType}"`, `"${delegatesList}"`, `Visite ${v.visitIndex || 1}`, `"${v.dateFull || ''}"`,
          `"${v.delegateName || ''}"`, `"${cycleName}"`, `"${weekStr}"`, `"${periodStr}"`, `"${planningStatus}"`
        ]);
      });
    });

    const csvContent = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `visites_rapprochees_cycles_${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    message.success("Export téléchargé !");
  };

  // ---------------------------------------------------------
  // ALERTES 2 : CONFLITS D'ASSIGNATION CROISÉS (MÊME SEMAINE)
  // ---------------------------------------------------------
  const fetchAssignmentConflicts = useCallback(async () => {
    setLoadingConflicts(true);
    try {
      const params = {};
      if (selectedDelegateFilter) params.delegateId = selectedDelegateFilter;
      if (selectedDateRangeFilter && selectedDateRangeFilter[0] && selectedDateRangeFilter[1]) {
        params.startDate = selectedDateRangeFilter[0].format('YYYY-MM-DD');
        params.endDate = selectedDateRangeFilter[1].format('YYYY-MM-DD');
      }
      const res = await getAllDoctorConflicts(params);
      setAssignmentConflicts(res.data || []);
    } catch (e) {
      console.error("Erreur chargement conflits assignation:", e);
    } finally {
      setLoadingConflicts(false);
    }
  }, [selectedDelegateFilter, selectedDateRangeFilter]);

  useEffect(() => {
    fetchAssignmentConflicts();
  }, [fetchAssignmentConflicts]);

  const handleExportAssignmentConflicts = () => {
    if (!assignmentConflicts || assignmentConflicts.length === 0) {
      message.warning("Aucun conflit d'assignation à exporter.");
      return;
    }

    const headers = [
      "ID Médecin", "Médecin", "Spécialité", "Région", "Partenaire",
      "Délégué 1", "Cycle 1", "Semaine 1", "Période 1",
      "Délégué 2", "Cycle 2", "Semaine 2", "Période 2"
    ];

    const rows = assignmentConflicts.map(item => [
      item.personId,
      `"${item.doctorName || ''}"`,
      `"${item.specialtyName || ''}"`,
      `"${item.regionName || ''}"`,
      item.isPartner ? "OUI" : "NON",
      `"${item.delegate1Name || ''}"`,
      `"${item.cycle1Name || ''}"`,
      `"Semaine ${item.week1Number}"`,
      `"${item.week1Start} au ${item.week1End}"`,
      `"${item.delegate2Name || ''}"`,
      `"${item.cycle2Name || ''}"`,
      `"Semaine ${item.week2Number}"`,
      `"${item.week2Start} au ${item.week2End}"`
    ]);

    const csvContent = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `conflits_assignation_medecins_${dayjs().format('YYYY-MM-DD')}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    message.success("Export des conflits téléchargé avec succès !");
  };

  const conflictTableColumns = [
    {
      title: 'Médecin',
      key: 'doctor',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Space>
            <Text strong style={{ color: '#1890ff', cursor: 'pointer' }} onClick={() => handleOpenDoctorHistory(r.personId)}>
              {r.doctorName}
            </Text>
            {r.isPartner && <Tag color="gold"><StarFilled /> Partenaire</Tag>}
          </Space>
          <Space size={4}>
            <Tag color="blue">{r.specialtyName}</Tag>
            <Tag color="cyan">{r.regionName}</Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Premier Délégué & Planning',
      key: 'plan1',
      render: (_, r) => (
        <div style={{ background: '#fff7e6', padding: '6px 10px', borderRadius: 4, border: '1px solid #ffd591' }}>
          <div><Text strong>👤 {r.delegate1Name}</Text></div>
          <div><Tag color="orange">{r.cycle1Name} (S{r.week1Number})</Tag></div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.week1Start} au {r.week1End}</div>
        </div>
      ),
    },
    {
      title: 'Second Délégué en Conflit',
      key: 'plan2',
      render: (_, r) => (
        <div style={{ background: '#fff1f0', padding: '6px 10px', borderRadius: 4, border: '1px solid #ffa39e' }}>
          <div><Text strong>👤 {r.delegate2Name}</Text></div>
          <div><Tag color="red">{r.cycle2Name} (S{r.week2Number})</Tag></div>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>{r.week2Start} au {r.week2End}</div>
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, r) => (
        <Button
          size="small"
          type="primary"
          ghost
          icon={<AuditOutlined />}
          onClick={() => handleOpenDoctorHistory(r.personId)}
        >
          Parcours
        </Button>
      ),
    },
  ];

  // ---------------------------------------------------------
  // PARCOURS DE VISITE DU MÉDECIN (MODAL)
  // ---------------------------------------------------------
  const handleOpenDoctorHistory = async (personId) => {
    setDoctorModalVisible(true);
    setDoctorLoading(true);
    try {
      const res = await axiosInstance.get('/v1/delegate-stocks/analytics/doctor-full-details', {
        params: { personId, startDate: '2020-01-01', endDate: dayjs().format('YYYY-MM-DD'), _t: Date.now() }
      });
      setDoctorDetailData(res.data || []);
    } catch (e) {
      message.error("Erreur chargement parcours médecin.");
    } finally {
      setDoctorLoading(false);
    }
  };

  // ---------------------------------------------------------
  // FILTRAGE ET COLONNES DU TABLEAU PRINCIPAL
  // ---------------------------------------------------------
  const filteredCycles = useMemo(() => {
    let tempCycles = [...(allCycles || [])];
    if (selectedDelegateFilter != null) {
      tempCycles = tempCycles.filter(cycle => String(cycle.delegate?.id) === String(selectedDelegateFilter));
    }
    const [filterStartDate, filterEndDate] = selectedDateRangeFilter;
    if (filterStartDate || filterEndDate) { 
      tempCycles = tempCycles.filter(cycle => { 
        const { overallStartDate: s, overallEndDate: e } = getCyclePeriod(cycle.weeks); 
        if (!s || !e) return false; 
        const dS = dayjs(s); 
        const dE = dayjs(e); 
        const sAFE = filterEndDate ? dS.isAfter(filterEndDate, 'day') : false; 
        const eBFS = filterStartDate ? dE.isBefore(filterStartDate, 'day') : false; 
        return !(sAFE || eBFS); 
      }); 
    }
    return tempCycles;
  }, [allCycles, selectedDelegateFilter, selectedDateRangeFilter]);

  const expandedRowRender = (record) => {
    const sortedWeeks = [...(record.weeks || [])].sort((a, b) => (a.week_number ?? Infinity) - (b.week_number ?? Infinity));
    
    const weekItems = sortedWeeks.map((week) => {
        const personsInWeek = week.persons || [];
        return (
            <Descriptions.Item 
                key={week.id || `week-${week.week_number}`} 
                label={<Text strong>Semaine {week.week_number}</Text>} 
                labelStyle={{ width: '150px', verticalAlign: 'top' }}
            >
                <div>
                    <Text>{formatDate(week.start_date)} au {formatDate(week.end_date)}</Text>
                    
                    {personsInWeek.length > 0 && (
                        <div style={{ marginTop: '8px', borderLeft: '2px solid #1890ff', paddingLeft: '16px' }}>
                            <Text strong>Médecins à visiter ({personsInWeek.length}):</Text>
                            <List
                                size="small"
                                dataSource={personsInWeek}
                                renderItem={person => (
                                    <List.Item style={{ padding: '2px 0', border: 'none' }}>
                                        <UserOutlined style={{ marginRight: 8, color: '#595959' }} />
                                        {`${person.firstname || ''} ${person.lastname || ''}`.trim()}
                                    </List.Item>
                                )}
                                style={{ maxHeight: '150px', overflowY: 'auto' }}
                            />
                        </div>
                    )}
                </div>
            </Descriptions.Item>
        );
    });

    const allAssignedPersons = record.persons || [];
    const personCount = allAssignedPersons.length;
    
    return (
        <Descriptions bordered size="small" column={1} style={{ background: '#f9f9f9', padding: '12px 24px', margin: '0 48px' }}>
            {weekItems.length > 0 ? weekItems : <Descriptions.Item label={<Text strong>Périodes</Text>}><Text type="secondary">Aucune période définie.</Text></Descriptions.Item>}
            
            <Descriptions.Item 
                key="all_persons_summary" 
                label={<Text strong>Total Personnes Uniques</Text>}
                labelStyle={{ width: '150px' }}
            >
                {personCount > 0 ? `${personCount} personne(s) assignée(s) au total dans ce cycle.` : 'Aucune personne assignée.'}
            </Descriptions.Item>
        </Descriptions>
    );
  };

  const columns = [
    { title: 'Nom du Cycle', dataIndex: 'name', key: 'name', render: (text) => text || <Text type="secondary">Sans Nom</Text>, ellipsis: true },
    { title: 'Délégué', key: 'delegate', render: (_, r) => r.delegate ? `${r.delegate.firstname} ${r.delegate.lastname}` : 'N/A', ellipsis: true },
    { title: 'Période Cycle', key: 'cyclePeriod', width: 180, align: 'center', render: (_, r) => { const { overallStartDate: s, overallEndDate: e } = getCyclePeriod(r.weeks); return (s && e) ? `${formatDate(s)} - ${formatDate(e)}` : <Text type="secondary">N/D</Text>; }, sorter: (a, b) => { const sA = getCyclePeriod(a.weeks).overallStartDate; const sB = getCyclePeriod(b.weeks).overallStartDate; if (!sA) return 1; if (!sB) return -1; return dayjs(sA).unix() - dayjs(sB).unix(); }, defaultSortOrder: 'descend' },
    { title: 'Nb Sem.', dataIndex: ['weeks', 'length'], key: 'weeksCount', align: 'center', width: 80 },
    { title: 'Obj/Sem', dataIndex: 'target_visits_per_week', key: 'target_visits_per_week', align: 'center', width: 80 },
    { 
        title: 'Nb Pers.', 
        key: 'personsCount', 
        align: 'center', 
        width: 80,
        render: (_, record) => {
            const allPersonIds = new Set((record.weeks || []).flatMap(week => (week.persons || []).map(p => p.id)));
            return allPersonIds.size;
        }
    },
    {
        title: 'Actions',
        key: 'actions',
        align: 'center',
        width: 180,
        fixed: 'right',
        render: (_, record) => (
            <Space size="small">
                <Button 
                    icon={<EditOutlined />} 
                    onClick={() => showEditModal(record)} 
                    title="Modifier le Cycle et la Planification" 
                    size="small" 
                    type="primary" 
                    ghost 
                />
                
                <Button 
                    icon={<UploadOutlined />} 
                    onClick={() => handleOpenWeeklyPlanUploadModal(record)} 
                    title="Uploader un Plan Hebdomadaire (CSV)" 
                    size="small" 
                />
                
                <Button 
                    icon={<DownloadOutlined />} 
                    onClick={() => handleDownloadWeeklyPlan(record.id, record.name)} 
                    title="Télécharger le Plan Hebdomadaire (CSV)" 
                    size="small" 
                    disabled={!record.weeks || !record.weeks.some(week => week.persons && week.persons.length > 0)}
                />
                
                <Popconfirm title="Supprimer ce cycle ?" onConfirm={() => handleDelete(record.id)} okText="Oui" cancelText="Non" okButtonProps={{ danger: true }}>
                    <Button icon={<DeleteOutlined />} danger title="Supprimer le Cycle" size="small" />
                </Popconfirm>
            </Space>
        )
    }
  ];

  return (
    <>
      <Title level={3} style={{ marginBottom: '16px' }}>Gestion des Cycles de Visite</Title>

      {/* --- ZONE DE FILTRES ET BOUTONS D'IMPORTATION --- */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px', padding: '16px 16px 0 16px', background: '#f0f2f5', borderRadius: '8px' }}>
         <Col xs={24} sm={24} md={18} lg={16}>
             <Row gutter={[16, 8]}>
                 <Col xs={24} lg={10}>
                     <Text strong style={{ display: 'block', marginBottom: '8px' }}><FilterOutlined /> Délégué:</Text>
                     <Select 
                        style={{ width: '100%' }} 
                        placeholder="Tous" 
                        loading={loadingDelegates} 
                        onChange={handleDelegateFilterChange} 
                        value={selectedDelegateFilter} 
                        allowClear 
                        showSearch 
                        optionFilterProp="label" 
                        filterOption={(i, o) => (o?.label ?? '').toLowerCase().includes(i.toLowerCase())} 
                        notFoundContent={loadingDelegates ? <Spin size="small" /> : "Aucun"} 
                        options={[{ value: undefined, label: 'Tous les délégués' }, ...delegatesList.map(d => ({ value: d.id, label: `${d.firstname || ''} ${d.lastname || ''}`.trim() || `ID:${d.id}` }))]} 
                     />
                     {errorDelegates && <Alert message={errorDelegates} type="warning" showIcon style={{ marginTop: 8 }} />}
                 </Col>
                 <Col xs={24} lg={14}>
                     <Text strong style={{ display: 'block', marginBottom: '8px' }}><CalendarOutlined /> Période:</Text>
                     <RangePicker style={{ width: '100%' }} value={selectedDateRangeFilter} onChange={handleDateRangeFilterChange} format={dateFormat} allowClear placeholder={['Début Cycle', 'Fin Cycle']} />
                 </Col>
             </Row>
         </Col>

         <Col xs={24} sm={24} md={6} lg={8} style={{ textAlign: 'right', alignSelf: 'flex-start' }}>
            <Space direction="vertical" align="end" style={{ width: '100%' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal} style={{ marginBottom: 8 }}> Ajouter un Cycle </Button>
                <Text strong style={{ fontSize: '0.9em' }}>Importer Cycles via CSV :</Text>
                <Space.Compact style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Upload {...uploadCycleProps} style={{ flexShrink: 1 }}>
                        <Button icon={<UploadOutlined />} loading={uploadingCycles && !parsedCycleData}> {cycleFileList.length > 0 ? cycleFileList[0].name : 'Choisir Fichier Cycles'} </Button>
                    </Upload>
                    <Button type={parsedCycleData && !parseCycleError ? "primary" : "default"} onClick={handleImportParsedCycleData} disabled={!parsedCycleData || parsedCycleData.length === 0 || !!parseCycleError || uploadingCycles} loading={uploadingCycles && !!parsedCycleData} > {uploadingCycles ? 'Import...' : 'Importer Cycles'} </Button>
                 </Space.Compact>
                 <Text type="secondary" style={{ fontSize: '0.8em' }}><Link href="/templates/cycle_import_template.csv" target="_blank" download="cycle_import_template.csv">Télécharger modèle (Cycles)</Link></Text>
                 {parseCycleError && (<Alert message={parseCycleError} type="error" showIcon style={{ width: '100%', textAlign: 'left', marginTop: 5 }} />)}
                 {parsedCycleData && parsedCycleData.length > 0 && !parseCycleError && (<Alert message={`${parsedCycleData.length} cycles prêts.`} type="success" showIcon style={{ width: '100%', textAlign: 'left', marginTop: 5 }} />)}
             </Space>
         </Col>
      </Row>

      {planningError && !loadingCycles && (
        <Alert message="Erreur chargement cycles" description={planningError?.message || JSON.stringify(planningError)} type="error" showIcon closable style={{ marginBottom: 16 }} />
      )}

      {/* --- TABLEAU DES CYCLES --- */}
      <Table
        columns={columns}
        dataSource={filteredCycles}
        loading={loadingCycles}
        rowKey="id"
        bordered
        expandable={{ expandedRowRender, rowExpandable: record => record.weeks?.length > 0 || record.persons?.length > 0 }}
        size="small"
        locale={{ emptyText: loadingCycles ? 'Chargement...' : 'Aucun cycle trouvé pour ces filtres.' }}
        scroll={{ x: 'max-content' }}
        style={{ marginBottom: 24 }}
      />

      {/* ========================================================================= */}
      {/* --- BLOC 1 : CONFLITS D'ASSIGNATION DE MÉDECINS (MÊME SEMAINE / MULTI-DÉLÉGUÉS) --- */}
      {/* ========================================================================= */}
      <div style={{ marginBottom: 20 }}>
        <Collapse
          defaultActiveKey={['doctor_assignment_conflicts']}
          style={{ 
            borderRadius: 8, 
            border: '1px solid #ffa39e', 
            background: '#fff1f0' 
          }}
          items={[
            {
              key: 'doctor_assignment_conflicts',
              label: (
                <Space>
                  <WarningOutlined style={{ color: '#cf1322', fontSize: '16px' }} />
                  <span style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '14px' }}>
                    Alertes : Conflits d'Assignation Médecins (Même semaine / Multi-Délégués)
                  </span>
                  <Badge count={assignmentConflicts.length} style={{ backgroundColor: '#ff4d4f' }} />
                </Space>
              ),
              extra: (
                <Space onClick={e => e.stopPropagation()}>
                  <Button 
                    size="small" 
                    icon={<DownloadOutlined />} 
                    onClick={handleExportAssignmentConflicts}
                    disabled={assignmentConflicts.length === 0}
                    style={{ borderColor: '#ffa39e', color: '#cf1322' }}
                  >
                    Exporter CSV
                  </Button>
                  <Button 
                    size="small" 
                    icon={<ReloadOutlined />} 
                    onClick={fetchAssignmentConflicts}
                  >
                    Actualiser
                  </Button>
                </Space>
              ),
              children: (
                <Spin spinning={loadingConflicts}>
                  {assignmentConflicts.length > 0 ? (
                    <Table
                      dataSource={assignmentConflicts}
                      columns={conflictTableColumns}
                      rowKey={(r, idx) => `${r.personId}-${r.cycle1Id}-${r.cycle2Id}-${idx}`}
                      pagination={{ pageSize: 5, size: 'small' }}
                      size="small"
                      bordered
                      style={{ background: '#fff' }}
                    />
                  ) : (
                    <Alert 
                      message="Aucun conflit d'assignation simultanée détecté pour les filtres actifs." 
                      type="success" 
                      showIcon 
                    />
                  )}
                </Spin>
              )
            }
          ]}
        />
      </div>

      {/* ========================================================================= */}
      {/* --- BLOC 2 : VISITES RAPPROCHÉES (< 5 JOURS) --- */}
      {/* ========================================================================= */}
      <div style={{ marginBottom: 20 }}>
        <Collapse
          defaultActiveKey={['frequent_visits']}
          style={{ 
            borderRadius: 8, 
            border: '1px solid #ffadd2', 
            background: '#fff0f6' 
          }}
          items={[
            {
              key: 'frequent_visits',
              label: (
                <Space>
                  <ClockCircleOutlined style={{ color: '#c41d7f', fontSize: '16px' }} />
                  <span style={{ color: '#c41d7f', fontWeight: 'bold', fontSize: '14px' }}>
                    Alertes : Visites Rapprochées (&lt; 5 jours)
                  </span>
                  <Badge count={frequentVisitsAlerts.length} style={{ backgroundColor: '#eb2f96' }} />
                </Space>
              ),
              extra: (
                <Space onClick={e => e.stopPropagation()}>
                  <Button 
                    size="small" 
                    icon={<DownloadOutlined />} 
                    onClick={handleExportFrequentVisits}
                    disabled={frequentVisitsAlerts.length === 0}
                    style={{ borderColor: '#ffadd2', color: '#c41d7f' }}
                  >
                    Exporter CSV
                  </Button>
                  <Button 
                    size="small" 
                    icon={<ClockCircleOutlined />} 
                    onClick={fetchFrequentVisits}
                  >
                    Actualiser
                  </Button>
                </Space>
              ),
              children: (
                <Spin spinning={loadingFrequentVisits}>
                  {frequentVisitsAlerts.length > 0 ? (
                    <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                      <List
                        itemLayout="horizontal"
                        dataSource={frequentVisitsAlerts}
                        renderItem={item => (
                          <List.Item
                            style={{ 
                              padding: '12px 16px', 
                              background: '#ffffff', 
                              borderRadius: 6, 
                              marginBottom: 8, 
                              border: '1px solid #ffd6e7' 
                            }}
                            actions={[
                              <Button 
                                size="small" 
                                type="primary" 
                                ghost 
                                icon={<AuditOutlined />} 
                                onClick={() => handleOpenDoctorHistory(item.personId)}
                              >
                                Parcours
                              </Button>
                            ]}
                          >
                            <List.Item.Meta
                              avatar={
                                <Avatar 
                                  icon={<UserOutlined />} 
                                  style={{ backgroundColor: '#ffd6e7', color: '#c41d7f', cursor: 'pointer' }} 
                                  onClick={() => handleOpenDoctorHistory(item.personId)}
                                />
                              }
                              title={
                                <Space wrap align="center">
                                  <Text 
                                    strong 
                                    style={{ cursor: 'pointer', color: '#1890ff', fontSize: '14px' }} 
                                    onClick={() => handleOpenDoctorHistory(item.personId)}
                                  >
                                    {item.name}
                                  </Text>
                                  {item.isPartner && <Tag color="gold"><StarFilled /> PARTENAIRE</Tag>}
                                  <Tag color="blue">{item.specialtyName}</Tag>
                                  <Tag color="cyan">{item.regionName}</Tag>
                                  <Tag color="error" style={{ fontWeight: 'bold' }}>
                                    ⚠️ {item.totalVisitsCount} visites en {item.totalSpanDays}
                                  </Tag>
                                  {item.isSameDelegate ? (
                                    <Tag color="magenta">Même délégué : {item.delegatesNamesList[0]}</Tag>
                                  ) : (
                                    <Tag color="purple">👥 {item.uniqueDelegatesCount} délégués : {item.delegatesNamesList.join(', ')}</Tag>
                                  )}
                                </Space>
                              }
                              description={
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                                  {item.visits?.map((v, vIdx) => (
                                    <div 
                                      key={vIdx} 
                                      style={{ 
                                        background: vIdx % 2 === 0 ? '#fff0f6' : '#f9f0ff', 
                                        padding: '6px 10px', 
                                        borderRadius: 4, 
                                        border: vIdx % 2 === 0 ? '1px solid #ffadd2' : '1px solid #d3adf7',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 6
                                      }}
                                    >
                                      <div>
                                        <Text strong style={{ color: vIdx % 2 === 0 ? '#c41d7f' : '#531dab', fontSize: '11px', marginRight: 8 }}>
                                          Visite {v.visitIndex} :
                                        </Text>
                                        <Tag color="default" style={{ fontSize: '11px' }}>{v.dateFull}</Tag>
                                        <Text strong style={{ fontSize: '11px', marginLeft: 4 }}>👤 {v.delegateName}</Text>
                                      </div>

                                      {v.context ? (
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                          <Tag color="geekblue" style={{ fontSize: '11px', margin: 0 }}>
                                            {v.context.cycleName} - S{v.context.weekNumber} ({v.context.startShort} au {v.context.endShort})
                                          </Tag>
                                          {v.context.isPlannedThisWeek ? (
                                            <Tag color="green" style={{ fontSize: '11px', margin: 0 }}>✅ Prévu cette semaine</Tag>
                                          ) : v.context.plannedInOtherWeek ? (
                                            <Tag color="orange" style={{ fontSize: '11px', margin: 0 }}>⚠️ Prévu en S{v.context.plannedInOtherWeek}</Tag>
                                          ) : (
                                            <Tag color="default" style={{ fontSize: '11px', margin: 0 }}>❌ Hors planning</Tag>
                                          )}
                                        </div>
                                      ) : (
                                        <Text type="secondary" style={{ fontSize: '11px' }}>Hors cycle actif à cette date</Text>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                  ) : (
                    <Alert message="Aucune visite rapprochée (< 5 jours) détectée pour ces filtres." type="info" showIcon />
                  )}
                </Spin>
              )
            }
          ]}
        />
      </div>

      {/* --- MODAL AJOUT / MODIFICATION CYCLE --- */}
      <Modal
          open={isAddEditModalVisible}
          onCancel={handleAddEditCancel}
          footer={null}
          destroyOnClose
          width={900}
          maskClosable={false}
          title={editingCycle ? 'Modifier le Cycle' : 'Ajouter un Cycle'}
      >
        <CycleFormModal
            visible={isAddEditModalVisible}
            onCancel={handleAddEditCancel}
            cycle={editingCycle}
        />
      </Modal>

      {/* --- MODAL ASSIGNATION DE PERSONNES CSV --- */}
      <Modal
          title={`Gérer Personnes pour Cycle: ${currentCycleForPersonManagement?.name || 'ID ' + currentCycleForPersonManagement?.id}`}
          open={isPersonUploadModalVisible}
          onCancel={handlePersonUploadModalCancel}
          footer={[
              <Button key="back" onClick={handlePersonUploadModalCancel}>
                  Fermer
              </Button>,
              <Button
                  key="submit"
                  type="primary"
                  loading={uploadingPersons}
                  onClick={triggerManualPersonUpload}
                  disabled={!fileToUploadForPersons || uploadingPersons}
              >
                  Uploader et Assigner Personnes
              </Button>,
          ]}
          destroyOnClose
          width={600}
      >
          <Upload.Dragger {...personUploadProps}>
              <p className="ant-upload-drag-icon">
                  <UsergroupAddOutlined />
              </p>
              <p className="ant-upload-text">Cliquez ou glissez un fichier CSV ici</p>
              <p className="ant-upload-hint">
                  Le fichier CSV doit contenir une colonne "ID" (ou "PersonID", "DoctorID", etc.)
                  avec les identifiants numériques des personnes à assigner.
                  <br/>
                  <Link href="/templates/persons_for_cycle_template.csv" target="_blank" download="persons_for_cycle_template.csv">
                      Télécharger modèle (Personnes)
                  </Link>
              </p>
          </Upload.Dragger>
          {uploadingPersons && <Spin tip="Assignation des personnes en cours..." style={{ marginTop: 16, display: 'block', textAlign: 'center' }} />}
          {personUploadResult && (
              <div style={{ marginTop: 16 }}>
                  {personUploadResult.message && (
                      <Alert
                          message={personUploadResult.message}
                          type={personUploadResult.errors && personUploadResult.errors.length > 0 ? "warning" : "success"}
                          showIcon
                      />
                  )}
                  {personUploadResult.errors && personUploadResult.errors.length > 0 && (
                      <List
                          size="small"
                          header={<div style={{fontWeight: 'bold'}}>Avertissements/Erreurs :</div>}
                          bordered
                          dataSource={personUploadResult.errors}
                          renderItem={(item) => <List.Item><Text type="danger">{item}</Text></List.Item>}
                          style={{ maxHeight: 150, overflowY: 'auto', marginTop: 8 }}
                      />
                  )}
              </div>
          )}
      </Modal>

      {/* --- MODAL UPLOAD PLAN HEBDOMADAIRE CSV --- */}
      <Modal
          title={`Uploader le Plan Hebdomadaire pour: ${currentCycleForPersonManagement?.name}`}
          open={isWeeklyPlanUploadModalVisible}
          onCancel={handleWeeklyPlanUploadModalCancel}
          footer={[
              <Button key="back" onClick={handleWeeklyPlanUploadModalCancel}>Annuler</Button>,
              <Button
                  key="submit"
                  type="primary"
                  loading={uploadingWeeklyPlan}
                  onClick={triggerManualWeeklyPlanUpload}
                  disabled={!weeklyPlanFile}
              >
                  Mettre à jour le Plan
              </Button>,
          ]}
          destroyOnClose
      >
          <Upload.Dragger
              name="file"
              multiple={false}
              beforeUpload={handleBeforeWeeklyPlanUpload}
              onRemove={() => setWeeklyPlanFile(null)}
              accept=".csv"
          >
              <p className="ant-upload-drag-icon"><UsergroupAddOutlined /></p>
              <p className="ant-upload-text">Cliquez ou glissez un fichier CSV ici</p>
              <p className="ant-upload-hint">Le fichier doit contenir les colonnes 'ID' et 'NumeroSemaine'.</p>
          </Upload.Dragger>
      </Modal>

      {/* --- MODAL HISTORIQUE DU PARCOURS MÉDECIN --- */}
      <Modal 
        title={<span><AuditOutlined /> Parcours de visite du Médecin</span>} 
        open={doctorModalVisible} 
        onCancel={() => setDoctorModalVisible(false)} 
        footer={null} 
        width={1000} 
        zIndex={2000}
      >
        <Spin spinning={doctorLoading}>
          {doctorDetailData.length > 0 && (
            <div style={{ marginBottom: 16, padding: 12, background: '#f0f5ff', borderRadius: 6, border: '1px solid #adc6ff' }}>
              <Title level={4} style={{ margin: 0, color: '#002766' }}>
                <UserOutlined /> {doctorDetailData[0].doctorName}
                {doctorDetailData[0].isPartner && <StarFilled style={{ color: '#fadb14', marginLeft: 8 }} />}
              </Title>
              <Space wrap style={{ marginTop: 6 }}>
                <Tag color="blue">{doctorDetailData[0].specialtyName}</Tag>
                <Tag color="cyan">{doctorDetailData[0].regionName || "N/A"}</Tag>
              </Space>
            </div>
          )}

          <List 
            dataSource={doctorDetailData} 
            locale={{ emptyText: "Aucun historique disponible." }}
            renderItem={item => (
              <List.Item style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ width: '25%' }}>
                    <Text strong>{dayjs(item.reportDate).format('DD/MM/YYYY')}</Text> <Text type="secondary">({dayjs(item.reportDate).format('HH:mm')})</Text>
                  </div>
                  <div style={{ width: '25%' }}>
                    <Tag icon={<UserOutlined />} color="default">{item.delegateName}</Tag>
                  </div>
                  <div style={{ width: '30%' }}>
                    {item.samples?.map((s, i) => (
                      <Tag key={i} color={s.isSpecific ? "error" : "processing"} style={{ marginBottom: 2 }}>{s.name} x{s.quantity}</Tag>
                    ))}
                  </div>
                  <div style={{ width: '20%' }}>
                    {item.products?.map((p, i) => (
                      <Tag key={i} color="orange" style={{ marginBottom: 2 }}>{p}</Tag>
                    ))}
                  </div>
                </div>
              </List.Item>
            )} 
          />
        </Spin>
      </Modal>
    </>
  );
}

export default CyclesPage;